import 'core-js/modules/es6.symbol';
import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import { ConnectedRouter } from 'connected-react-router';
import debounce from 'lodash/debounce';
import { loadableReady } from '@loadable/component';
import 'core-js/modules/es6.promise';
import 'core-js/modules/es6.array.iterator';
import 'core-js/modules/es6.object.freeze';
import 'core-js/modules/es6.object.assign';
import 'core-js/modules/es6.array.for-each';

import './polyfill';
import '../../../wydr';
import './abtest/init';
import './index.css';

import App from './App';
import historyInterceptor from './utility/history-interceptor';
import { addToCart } from './utility/utility-actions';
import { screenResize } from './ui/ui-action';
import configureStore from './store/store';
import ScrollToTop from './shared/components/scroll-to-top';
import { MetricsObserver } from './metrics/event-observer';
import getRoutes from './routes';
import { Helpers } from './core/src/helpers';
import getWorker from './worker';
import prefetchHPService from './prefetch-hpservice';

const HYDRATION_DELAY = 501;

const loadPolyfill = () => {
    const ua = window.navigator.userAgent;
    const supported =
        typeof Array.prototype.forEach === 'function' &&
        typeof Array.prototype.find === 'function' &&
        typeof Object.entries === 'function' &&
        typeof Object.values === 'function';
    return /MSIE|Trident|YandexBot/.test(ua) || !supported;
};

let initCalled = false;
document.addEventListener('readystatechange', event => {
    if (!initCalled && event.target.readyState === 'complete') {
        initCalled = true;
        init();
    }
});

if (document.readyState === 'complete') {
    if (!initCalled) {
        initCalled = true;
        init();
    }
}

setTimeout(() => {
    if (!initCalled) {
        initCalled = true;
        init();
    }
}, HYDRATION_DELAY * 7);

async function init(force) {
    if (loadPolyfill()) await import(/* webpackChunkName: "polyfill" */ './polyfill/ie-polyfill');
    const { location } = window;
    //test delaying hydration for blog pages
    if (!force && location && location.pathname && /\/us\/en\/tech-takes\/[^$]/.test(location.pathname)) {
        setTimeout(() => {
            init(true);
        }, 800);
        return;
    }
    const elem = document.getElementById('data');
    if (!elem) {
        load(undefined, process.env.CLIENT_RENDER);
        return;
    }

    let data = elem.innerHTML;
    data = data.trim();
    if (data.length < 10) {
        load();
        return;
    }
    data = data.slice(5, -4);

    try {
        const worker = getWorker();
        worker.postMessage({ payload: data });
        worker.onmessage = e => {
            load(e.data);
        };
        worker.onerror = e => {
            load(JSON.parse(data));
        };
    } catch (e) {
        load(JSON.parse(data));
    }
}

function load(__data, forceHydration) {
    let initialStore = {};
    const { location = {} } = window;
    //if server indicates store should be fetch async, fetch it and use that to load and hydate
    if (__data && __data.fetchState) {
        return axios
            .get(__data.fetchState)
            .then(({ data: stateJSON }) => {
                //prevent a loop here, just incase
                !stateJSON.fetchState && load(stateJSON);
            })
            .catch(e => {
                //not sure how you can recover from this????
            });
    }
    //skip this step if we don't want to delay hydration
    if (!forceHydration) {
        try {
            const startTime = new Date().getTime();
            return prefetchHPService(__data, HYDRATION_DELAY * 2)
                .then(() => {
                    //if we have successfully prefetched pricing info, delay hydration
                    //take the dalay of the dalay time and the time it took for HPservice to response
                    let timeoutRemaining = Math.max(HYDRATION_DELAY - (new Date().getTime() - startTime), 0);
                    setTimeout(() => {
                        load(__data, true);
                    }, timeoutRemaining);
                })
                .catch(e => {
                    load(__data, true);
                });
        } catch (e) {
            load(__data, true);
        }
    }

    if (__data && __data.router) {
        try {
            initialStore = __data;
            // cloudfront doesn't cache based on most query params, we need to update router info with query from browser
            initialStore.router.location = initialStore.router.location || {};
            initialStore.router.location.search = location.search;
            initialStore.router.location.hash = location.hash;
            initialStore.router.location.query = Helpers.getSearch(location.search);
            // initialStore.ui.width = innerWidth;
            // initialStore.ui.height = innerHeight;
        } catch (e) {}
    }

    const history = historyInterceptor(createBrowserHistory());

    try {
        if (process.env.CLIENT_RENDER) {
            //grab ab test param to force test on CSR
            const query = Helpers.getSearch(location.search);
            initialStore.testFlags = JSON.parse(query.abtest);
        }
        //set mpulse dimension
        window.testConfig =
            initialStore.slugInfo.templateKey ||
            (initialStore.pdp && initialStore.pdp.pdpConfig && initialStore.pdp.pdpConfig.templateKey);
    } catch (e) {}

    // inialize store with all available routes, since we only host a subset of urls in react
    // this helps us identify if an internal url can be handled by react router of if needs to direct to ETR
    // this is set on the server, incase SSR is not working initialize it for CSR
    initialStore.routes = initialStore.routes || getRoutes('', true);
    const store = configureStore(history, initialStore);
    const { dispatch, getState } = store;
    window.store = store;

    /**
     * Add single event listener for window resize store dimensions in redux
     */
    window.addEventListener(
        'resize',
        debounce(() => {
            store.dispatch(screenResize(window.innerWidth, window.innerHeight));
        }, 50)
    );

    // expose redux action in global scope
    window.appActions = window.appActions || {};
    window.appActions.addToCart = (product, qty, blockFlyout) =>
        addToCart(product, qty, blockFlyout)(dispatch, getState);
    window.appActions.changeLocation = path => history.push(path, getState());

    window.appActions.events = {
        subscribe: (event, callback) => MetricsObserver.subscribe(event, callback),
        unsubscribe: (event, callback) => MetricsObserver.unsubscribe(event, callback),
    };

    const render = renderMethod => {
        renderMethod(
            <Provider store={store}>
                <ConnectedRouter history={history}>
                    <ScrollToTop>
                        <App dispatch={store.dispatch} />
                    </ScrollToTop>
                </ConnectedRouter>
            </Provider>,
            document.getElementById('root')
        );
    };

    if (process.env.CLIENT_RENDER) {
        render(ReactDOM.render);
        module.hot.accept();
    } else {
        loadableReady(() => {
            render(ReactDOM.hydrate);
        });
    }
}
